// Deterministic quick investment score (0-100)
// Kullanıcının verdiği verilerden anlık, tekrar üretilebilir bir puan üretir.

const WEIGHTS = {
  location: 0.20,
  price: 0.20,
  rent_yield: 0.15,
  condition: 0.15,
  legal: 0.10,
  parking_site: 0.08,
  usability: 0.07,
  negotiation: 0.05,
};

const STATUS_MULTIPLIER = {
  "Yeni Eklendi": 0.9,
  "Elendi": 0.3,
  "Beklemede": 0.85,
};

export function calculateQuickScore(p) {
  const factors = {};
  const filled = {};
  const positives = [];
  const negatives = [];
  const missing = [];

  // Location
  let locScore = 50;
  if (p.location_score) { locScore = p.location_score * 10; filled.location = true; }
  else if (p.city && p.district) { locScore = 60; filled.location = true; }
  else if (p.location) { locScore = 50; filled.location = true; }
  else missing.push("Konum");
  factors.location = locScore;

  // Price
  const currentPrice = p.current_price || p.price;
  const netArea = p.net_area || p.area;
  let priceScore = 50;
  if (currentPrice && netArea) {
    const ppm2 = currentPrice / netArea;
    if (ppm2 < 25000) priceScore = 92;
    else if (ppm2 < 50000) priceScore = 78;
    else if (ppm2 < 80000) priceScore = 65;
    else if (ppm2 < 120000) priceScore = 55;
    else if (ppm2 < 180000) priceScore = 42;
    else priceScore = 30;
    if (p.my_offer && p.my_offer < currentPrice) {
      const discount = (currentPrice - p.my_offer) / currentPrice;
      priceScore = Math.min(100, priceScore + discount * 40);
      positives.push(`Teklif indirimi %${(discount * 100).toFixed(1)}`);
    }
    filled.price = true;
  } else {
    if (!currentPrice) missing.push("Güncel fiyat");
    if (!netArea) missing.push("Net m²");
  }
  factors.price = priceScore;

  // Rent yield
  let rentScore = 50;
  if (p.estimated_rent && currentPrice) {
    const annualYield = (p.estimated_rent * 12) / currentPrice;
    if (annualYield >= 0.08) rentScore = 92;
    else if (annualYield >= 0.06) rentScore = 78;
    else if (annualYield >= 0.05) rentScore = 68;
    else if (annualYield >= 0.04) rentScore = 58;
    else if (annualYield >= 0.03) rentScore = 48;
    else rentScore = 35;
    filled.rent_yield = true;
    if (annualYield >= 0.06) positives.push(`Brüt kira getirisi %${(annualYield * 100).toFixed(1)}`);
  } else {
    missing.push("Tahmini aylık kira");
  }
  factors.rent_yield = rentScore;

  // Condition
  const condMap = { "Çok iyi": 92, "İyi": 78, "Orta": 60, "Tadilat gerekli": 42, "Ağır tadilat gerekli": 25 };
  if (p.technical_condition && condMap[p.technical_condition]) {
    factors.condition = condMap[p.technical_condition];
    filled.condition = true;
    if (factors.condition < 50) negatives.push(`Teknik durum: ${p.technical_condition}`);
  } else { factors.condition = 55; missing.push("Teknik durum"); }

  // Legal
  const deedMap = { "Kat mülkiyeti": 92, "Kat irtifakı": 72, "Arsa tapusu": 65, "Hisseli tapu": 45 };
  if (p.deed_type && deedMap[p.deed_type]) {
    factors.legal = deedMap[p.deed_type];
    filled.legal = true;
    if (p.deed_type === "Hisseli tapu") negatives.push("Hisseli tapu — hukuki risk");
    if (p.deed_type === "Kat mülkiyeti") positives.push("Kat mülkiyeti tapusu");
  } else { factors.legal = 60; missing.push("Tapu türü"); }

  // Parking & site
  const feats = p.features || [];
  let ps = 50;
  if (p.parking) {
    if (["Kapalı otopark", "Açık ve kapalı", "Daireye tahsisli"].includes(p.parking)) ps += 25;
    else if (p.parking === "Açık otopark") ps += 15;
    else if (p.parking === "Yok") { ps -= 15; negatives.push("Otopark yok"); }
    filled.parking_site = true;
  }
  if (feats.includes("has_pool")) ps += 10;
  if (feats.includes("has_elevator")) ps += 5;
  factors.parking_site = Math.max(0, Math.min(100, ps));

  // Usability
  let us = 55;
  if (p.heating) {
    filled.usability = true;
    if (p.heating === "Isıtma yok") { us -= 15; negatives.push("Isıtma yok"); }
    else if (["Doğalgaz kombi", "Yerden ısıtma"].includes(p.heating)) us += 15;
  }
  if (feats.includes("has_natural_gas")) us += 5;
  factors.usability = Math.max(0, Math.min(100, us));

  // Negotiation
  let ns = 50;
  if (p.negotiation_desire === "yüksek") ns = 80;
  else if (p.negotiation_desire === "orta") ns = 60;
  else if (p.negotiation_desire === "düşük") ns = 40;
  if (feats.includes("open_to_negotiation")) { ns = Math.max(ns, 75); positives.push("Pazarlığa açık"); }
  if (feats.includes("urgent_sale")) { ns = Math.max(ns, 88); positives.push("Acil satış ihtimali"); }
  factors.negotiation = ns;
  if (p.negotiation_desire) filled.negotiation = true;

  // Negative signals
  const negLabels = { humidity: "Rutubet", noise: "Gürültü", dark: "Karanlık daire", facade_issue: "Dış cephe sorunu", roof_risk: "Çatı riski", parking_insufficient: "Otopark yetersiz", bathroom_vent: "Banyo havalandırma", common_area_neglect: "Ortak alanlar bakımsız" };
  Object.entries(negLabels).forEach(([k, l]) => {
    if (feats.includes(k)) {
      negatives.push(l);
      factors.condition = Math.max(0, factors.condition - 4);
      factors.usability = Math.max(0, factors.usability - 3);
    }
  });

  let raw =
    factors.location * WEIGHTS.location +
    factors.price * WEIGHTS.price +
    factors.rent_yield * WEIGHTS.rent_yield +
    factors.condition * WEIGHTS.condition +
    factors.legal * WEIGHTS.legal +
    factors.parking_site * WEIGHTS.parking_site +
    factors.usability * WEIGHTS.usability +
    factors.negotiation * WEIGHTS.negotiation;

  const statMul = STATUS_MULTIPLIER[p.status] ?? 1;
  raw = raw * statMul;
  const score = Math.round(Math.max(0, Math.min(100, raw)));
  const filledCount = Object.values(filled).filter(Boolean).length;
  const completeness = Math.round((filledCount / 8) * 100);

  return { score, factors, completeness, positives, negatives, missing, provisional: completeness < 50 };
}

export function verdictFromScore(score, completeness) {
  if (completeness < 30) return { label: "Veri yetersiz", color: "#6B7280" };
  if (score >= 80) return { label: "Güçlü aday", color: "#10B981" };
  if (score >= 65) return { label: "İncelenmeye değer", color: "#3B82F6" };
  if (score >= 50) return { label: "Şartlı aday", color: "#F59E0B" };
  if (score >= 35) return { label: "Zayıf aday", color: "#EF4444" };
  return { label: "Elenebilir", color: "#7F1D1D" };
}
